import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Typography } from '@mui/material';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import Button from 'shared/components/widgets/button';
import Autocomplete from 'shared/components/widgets/autocomplete';
import { ROUTES } from 'constants/routes';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import { Controller, useForm } from 'react-hook-form';
import AutocompleteContainer from 'shared/components/widgets/autocomplete-container';
import RouteLink from 'shared/components/widgets/route-link';
import { employeeUpdateService } from '@services/private/employee-management/employee-update.service';
import { is200xRange } from '@utils/utilities';
import { useEffect } from 'react';

const EmployeeManagementUpdate = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      employeeStatus: state?.staffMstDetail?.userSt || '50',
    },
  });

  const employeeStatusFilterData = [
    { label: '신청', value: '10' },
    { label: '정상', value: '50' },
    { label: '일시정지', value: '80' },
    { label: '퇴사', value: '90' },
  ];

  const updateEmployeeData = async (userSeq, userSt) => {
    const { response, statusCode } = await employeeUpdateService({
      userSeq,
      userSt,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      navigate(ROUTES.EMPLOYEE_MANAGEMENT.ROOT);
    }
  };

  const onSubmit = (data: any) => {
    const { employeeStatus } = data;
    updateEmployeeData(state?.staffMstDetail?.userSeq, employeeStatus);
  };

  useEffect(() => {
    setValue('employeeStatus', state?.staffMstDetail?.userSt || '50');
  }, []);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.EMPLOYEE_MANAGEMENT.ROOT}>Home</RouteLink>
          <RouteLink to={ROUTES.EMPLOYEE_MANAGEMENT.ROOT}>
            마크애니 직원 관리
          </RouteLink>
          <RouteLink to={ROUTES.EMPLOYEE_MANAGEMENT.ROOT} isActive>
            직원 정보 수정
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          마크애니 직원 관리
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={6} marginBottom="50px">
          <FieldBox>
            {/* SUBSCRIPTION CATEGORY */}
            <FieldBoxProperty
              name="가입구분"
              value={
                <Typography variant="body1" color="var(--gray-800)">
                  {(state?.staffMstDetail?.joinTp === 'markany' &&
                    '마크애니') ||
                    (state?.staffMstDetail?.joinTp === 'google' && 'Google') ||
                    (state?.staffMstDetail?.joinTp === 'kakao' && '카카오') ||
                    '--'}
                </Typography>
              }
            />
            {/* ID */}
            <FieldBoxProperty
              name="아이디"
              value={
                <Typography variant="body1" color="var(--gray-800)">
                  {state?.staffMstDetail?.userUid}
                </Typography>
              }
            />
            {/* NAME */}
            <FieldBoxProperty
              name="성명"
              value={
                <Typography variant="body1" color="var(--gray-800)">
                  {state?.staffMstDetail?.userName}
                </Typography>
              }
            />
            {/* EMAIL */}
            <FieldBoxProperty
              name="이메일"
              value={
                <Typography variant="body1" color="var(--gray-800)">
                  {state?.staffMstDetail?.userEmail}
                </Typography>
              }
            />
            {/* EMPLOYEE STATUS */}
            <FieldBoxProperty
              name="* 직원 상태"
              value={
                <AutocompleteContainer type="form">
                  <Controller
                    name="employeeStatus"
                    control={control}
                    render={({ field }) => {
                      return (
                        <Autocomplete
                          {...field}
                          options={employeeStatusFilterData}
                          defaultValue={
                            employeeStatusFilterData.find(
                              (el) => el.value === field.value,
                            ) ?? {
                              label: '정상',
                              value: '50',
                            }
                          }
                          value={field?.value ?? '50'}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          onChange={(_, newValue, reason) => {
                            field.onChange(newValue?.value);
                            if (reason === 'clear') {
                              field.onChange('50');
                            }
                          }}
                        />
                      );
                    }}
                  />
                </AutocompleteContainer>
              }
            />
          </FieldBox>
        </Box>
        <Box>
          <Box display="flex" gap="14px">
            {/* CANCEL */}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(ROUTES.EMPLOYEE_MANAGEMENT.ROOT)}
            >
              취소
            </Button>
            {/* SAVE */}
            <Button variant="contained" color="primary" type="submit">
              저장하기
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EmployeeManagementUpdate;
